import type { TFunction } from 'i18next';
import { toJS } from 'mobx';

import type { Campaign, DisplayCreative, FacebookDisplayCreative } from '@feathr/blackbox';
import { AdTagCreative, CreativeClass } from '@feathr/blackbox';
import {
  extractMetaFeedback,
  getMetaAdStatusLabel,
  mapMetaAdStatusToVisual,
} from '@feathr/extender/components/Creatives/Creatives.utils';

import {
  hasValidAspectRatio,
  validateAdTagCreative,
} from '../AdWizardCreativesStep/AdWizardCreativesStep.utils';

export type TCreativeStatus = 'ready' | 'warning' | 'error';

export interface ICreativeStatusInfo {
  status: TCreativeStatus;
  errors: string[];
}

/**
 * Derives the visual status and collects all error/warning messages for a creative
 * in a single pass. Validation errors always produce 'error'; if none, ad_status
 * determines the result.
 *
 * @param creative - The creative to evaluate
 * @param campaign - The campaign the creative belongs to
 * @param t - Translation function
 * @returns Status and array of error/warning messages
 */
export function getCreativeStatusInfo(
  creative: DisplayCreative,
  campaign: Campaign,
  t: TFunction,
): ICreativeStatusInfo {
  const validationErrors = getValidationErrors(creative, campaign);
  const errors = [...validationErrors];
  let status: TCreativeStatus = validationErrors.length > 0 ? 'error' : 'ready';

  if (creative.isFacebook) {
    const fbCreative = creative as FacebookDisplayCreative;
    const adStatus = fbCreative.get('ad_status');

    if (adStatus && adStatus !== 'ACTIVE') {
      const label = getMetaAdStatusLabel(adStatus, t);
      const feedback = extractMetaFeedback(fbCreative.get('ad_review_feedback'));
      errors.push(feedback ? `${label}: ${feedback}` : label);
    }

    if (status === 'ready' && adStatus) {
      status = mapMetaAdStatusToVisual(adStatus);
    }
  }

  return { status, errors };
}

/**
 * Collects field-level validation errors for a single creative.
 */
function getValidationErrors(creative: DisplayCreative, campaign: Campaign): string[] {
  let errors: string[] = toJS(
    creative.validate(['width', 'height', 'spec', 'destination_url', 'name'], false).errors,
  );

  const validDimensions = creative.getValidDimensions(campaign);
  const specs = Object.values(validDimensions).map((v) => v.spec);
  const creativeIsValidDimensions = specs.includes(creative.get('spec'));
  const creativeHasValidAspectRatio = hasValidAspectRatio(creative, validDimensions);

  if (!creativeIsValidDimensions && !creativeHasValidAspectRatio) {
    errors.push(
      `${creative.get('width')}x${creative.get('height')} does not match any standard ad size. Images must have exact aspect ratios and meet minimum dimensions.`,
    );
  }

  if (
    creative instanceof AdTagCreative &&
    (creative as AdTagCreative).get('_cls') === CreativeClass.DisplayAdTag
  ) {
    errors = errors.concat(validateAdTagCreative(creative as AdTagCreative));
  }

  return errors;
}
